<template>
    <v-container
        :id="`${baseId}-main-container`"
        class="main"
        :class="{
            'v-view-xs': $vuetify.breakpoint.xs,
            'v-view-sm': $vuetify.breakpoint.sm,
            'v-view-md': $vuetify.breakpoint.md,
            'v-view-lg': $vuetify.breakpoint.lg,
            'v-view-xl': $vuetify.breakpoint.xl,
            'v-view-sm-down': $vuetify.breakpoint.smAndDown,
            'v-view-sm-up': $vuetify.breakpoint.smAndUp,
            'v-view-md-down': $vuetify.breakpoint.mdAndDown,
            'v-view-md-up': $vuetify.breakpoint.mdAndUp,
            'v-view-lg-down': $vuetify.breakpoint.lgAndDown,
            'v-view-lg-up': $vuetify.breakpoint.lgAndUp
        }"
        fluid my-0 py-0
    >
        <!-- Introduction -->
        <v-layout
            :id="`${baseId}-introduction`"
            class="section section-introduction justify-center" row
            :style="{ 'background-image': getBackgroundImageByWidth(sectionIntroductionImages) }"
        >
            <v-flex :id="`${baseId}-introduction-hero-container`" class="section-container hero-content-container" xs12>
                <h2 :id="`${baseId}-introduction-hero-title-header`" class="section-heading tx-font-secondary" v-html="$nsLanguage.getTranslation('hero.title')"></h2>
                <v-flex :id="`${baseId}-introduction-hero-section-container`" class="section-content-container tx-em-bold-800" xs12 mt-7>
                    <div
                        :id="`${baseId}-anonymous-hero-text`"
                        class="section-text ma-0 mt-4 tx-font-primary tx-em-bold-800 tx-em-text-uppercase"
                        v-if="!loginStatus" v-html="$nsLanguage.getTranslation('hero.heroTextAnonymous')"
                    >
                    </div>
                    <div
                        :id="`${baseId}-signed-in-hero-text`"
                        class="section-text ma-0 mt-4 tx-font-primary tx-em-bold-800 tx-em-text-uppercase"
                        v-else-if="!outOfMarket && loginStatus && canOptIn && !optInStatus"
                        v-html="$nsLanguage.getTranslation('hero.heroTextSignedIn')"
                    >
                    </div>
                    <div
                        :id="`${baseId}-opted-in-hero-text`"
                        class="section-text ma-0 mt-4 tx-font-primary tx-em-bold-800 tx-em-text-uppercase"
                        v-else-if="!outOfMarket && loginStatus && optInStatus" v-html="$nsLanguage.getTranslation('hero.heroTextOptedIn')"
                    >
                    </div>
                    <div class="section-text ma-0 mt-4 tx-font-primary tx-em-bold-800 tx-em-text-uppercase semitransparent-box"
                         v-else-if="loginStatus && !canOptIn && !optInStatus"
                         v-html="$nsLanguage.getTranslation('hero.heroTextNoLoi')"
                         :id="`${baseId}-no-loi-hero-text`">
                    </div>
                    <div class="market_unavailable mt-4" v-if="outOfMarket" :id="`${baseId}-market-unavailable-button`">
                        {{ $nsLanguage.getTranslation('landingButton.marketUnavailable') }}
                    </div>
                    <div
                        :id="`${baseId}-maintenance-mode-box-text`"
                        class="maintenance-mode semitransparent-box"
                        v-else-if="loginStatus && inMaintenanceMode" v-html="$nsLanguage.getTranslation('maintenanceNoticeLandingPage')"
                    >
                    </div>
                    <div class="button-container mt-5" :id="`${baseId}-create-loyalty-account`">
                        <a
                            class="tx-dec-none"
                            :href="loginPage"
                            v-if="!busy && !loginStatus"
                            @click="publishEvent('loyaltyCreateAcct', { context: 'landing' })"
                            :id="`${baseId}-create-loyalty-account-link`"
                        >
                            <button class="primary-outline fluid button tx-font-primary text-center text-uppercase vertical_center" :id="`${baseId}-create-loyalty-account-button`">
                                <span :id="`${baseId}-create-loyalty-account-button-text`">{{ $nsLanguage.getTranslation('landingButton.createAccount') }}</span>
                            </button>
                        </a>
                        <a
                            class="tx-dec-none"
                            href="#"
                            v-if="!busy && !inMaintenanceMode && loginStatus && !optInStatus && !outOfMarket && canOptIn"
                            @click="openOptInDialog($event)"
                            :id="`${baseId}-opt-in-loyalty-account-link`"
                        >
                            <button class="primary-outline fluid button tx-font-primary text-center text-uppercase vertical_center" :id="`${baseId}-opt-in-loyalty-account-button`">
                                <span :id="`${baseId}-opt-in-loyalty-account-button-text`">{{ $nsLanguage.getTranslation('landingButton.startNow') }}</span>
                            </button>
                        </a>
                        <a
                            class="tx-dec-none"
                            :href="dashboardPage"
                            v-if="!busy && !inMaintenanceMode && loginStatus && optInStatus && !outOfMarket"
                            @click="publishEvent('loyaltyViewDash', { context: 'landing' })"
                            :id="`${baseId}-dashboard-loyalty-account-link`"
                        >
                            <button
                                class="primary-outline fluid button tx-font-primary text-center text-uppercase vertical_center"
                                :id="`${baseId}-dashboard-loyalty-account-button`"
                            >
                                <span :id="`${baseId}-dashboard-loyalty-account-button-text`">{{ $nsLanguage.getTranslation('landingButton.showDashboard') }}</span>
                            </button>
                        </a>
                    </div>
                </v-flex>
                <div class="hero-footer" :id="`${baseId}-hero-footer`">
                    <!-- Disclaimer -->
                    <ul class="disclaimers" v-html="$nsLanguage.getTranslation('tier.disclaimer')" :id="`${baseId}-hero-footer-disclaimer`"></ul>
                    <div class="product-logo" v-if="language && market" :id="`${baseId}-hero-footer-product-logo-div`">
                        <img :src="pageLogo" alt="" :id="`${baseId}-hero-footer-product-logo`" />
                    </div>
                </div>
            </v-flex>
        </v-layout>
        <!-- Rewards -->
        <v-layout :id="`${baseId}-rewards-section-container`" class="section section-rewards justify-center" row>
            <v-flex class="section-container" xs12 :id="`${baseId}-rewards-section`">
                <BrushstrokeHeading :parent-id="`${baseId}-rewards-section`" brushstroke-color="rgb( 194,212,46 )">
                    <template #title>
                        <span
                            :id="`${baseId}-rewards-section-brushstroke-heading-title-span`"
                            class="section-heading tx-font-primary tx-em-bold-600"
                            v-html="$nsLanguage.getTranslation('rewards.rewardsProgressTitle')"
                        >
                        </span>
                    </template>
                </BrushstrokeHeading>
                <p
                    :id="`${baseId}-rewards-section-progress-text`"
                    class="section-text tx-font-primary tx-em-bold-700 mt-8 mb-0 ml-auto mr-auto"
                    v-html="$nsLanguage.getTranslation('rewards.progressText')"
                >
                </p>
                <v-container fluid :id="`${baseId}-rewards-section-card-container`">
                    <v-layout class="section-box-max justify-space-around" row :id="`${baseId}-rewards-section-card-container-layout`">
                        <v-flex class="s-card-container" mt-0 v-for="(card, index) in rewardCards" :key="index" :id="`${baseId}-rewards-section-card-container-card-${index}`">
                            <CardWithSpace
                                class="s-card box-shadow-none"
                                space-alignment="top"
                                :parent-id="`${baseId}-rewards-section-card-container-card-${index}`"
                            >
                                <template #space>
                                    <div class="card-icon-container text-center" :id="`${baseId}-rewards-section-card-container-card-${index}-space-icon-container`" >
                                        <img class="card-icon" :src="card.icon" alt="" :id="`${baseId}-rewards-section-card-container-card-${index}-space-icon`"/>
                                    </div>
                                </template>
                                <template #content>
                                    <div class="card-content-container mt-6" :id="`${baseId}-rewards-section-card-container-card-${index}-content-container`">
                                        <v-card-title
                                            class="card-title text-center ma-0 pa-0"
                                            :id="`${baseId}-rewards-section-card-container-card-${index}-content-card-title-container`"
                                        >
                                            <h1 class="ma-0 pa-0 tx-font-primary" :id="`${baseId}-rewards-section-card-container-card-${index}-content-card-title`">
                                                {{ $nsLanguage.getTranslation(card.title) }}
                                            </h1>
                                        </v-card-title>
                                        <v-card-text
                                            class="card-text ma-0 mt-4 pa-0 tx-font-primary"
                                            :id="`${baseId}-rewards-section-card-container-card-${index}-content-card-text-container`"
                                        >
                                            <p :id="`${baseId}-rewards-section-card-container-card-${index}-content-card-text`">{{ $nsLanguage.getTranslation(card.text) }}</p>
                                        </v-card-text>
                                    </div>
                                </template>
                            </CardWithSpace>
                        </v-flex>
                    </v-layout>
                </v-container>
                <v-container fluid :id="`${baseId}-faq-section-container`">
                    <v-layout :id="`${baseId}-faq-section-layout`">
                        <v-flex :id="`${baseId}-faq-section-layout-flex`">
                            <p class="faq-link tx-font-primary" :id="`${baseId}-faq-section-faq-description`">
                                {{ $nsLanguage.getTranslation('faq.faqDescription') }}
                                <br v-if="isMobile" />
                                <a :href="faqPage" @click="publishEvent('loyaltyLearn', { context: 'landing' })" :id="`${baseId}-faq-section-faq-description-link`">
                                    {{ $nsLanguage.getTranslation('faq.faqLinkLabel') }}
                                </a>
                            </p>
                        </v-flex>
                    </v-layout>
                </v-container>
            </v-flex>
        </v-layout>
        <!-- Tiering -->
        <v-layout v-if="showTiering" :id="`${baseId}-tiering-section-container`" class="section section-group-tiering justify-center" row>
            <v-flex class="section-container" :id="`${baseId}-tiering-section-flex`" xs12>
                <BrushstrokeHeading :parent-id="`${baseId}-tiering-section`" brushstroke-color="#FFFFFF">
                    <template #title>
                        <span
                          class="section-heading tx-font-primary tx-color-white tx-em-bold-800"
                          v-html="$nsLanguage.getTranslation('tier.tierRewardsTitle')"
                          :id="`${baseId}-tiering-section-brushstroke-heading-title`"
                        >
                        </span>
                    </template>
                </BrushstrokeHeading>
                <div class="section-content" :id="`${baseId}-tiering-section-content`">
                    <template v-for="(tierGroup, index) in tiering">
                        <template v-if="tierGroup.configuration.view == 'group' && !tierGroup.configuration.hideInLanding">
                            <div :key="index" class="tiering-container" :id="`${baseId}-tiering-section-tier-group-${index}-group-card-container`">
                                <ns-tier-group-card
                                    class="tier-card"
                                    :layout="layout"
                                    :links="tieringLinks"
                                    :togglable="true"
                                    :configuration="tierGroup.configuration"
                                    :tiers="tierGroup.tiers"
                                    :parent-id="`${baseId}-tiering-section-tier-group-${index}-group-card`"
                                />
                            </div>
                        </template>
                        <template v-if="tierGroup.configuration.view == 'cards'">
                            <div :key="index" class="tiering-container" :id="`${baseId}-tiering-section-tier-group-${index}-white-card-container`">
                                <ns-white-tier-card
                                    class="ma-0 mt-4 mt-lg-10"
                                    :highlight="'basic'"
                                    :showLocked="true"
                                    :links="tieringLinks"
                                    :tiers="tierGroup.tiers"
                                    :parent-id="`${baseId}-tiering-section-tier-group-${index}-white-tier-card`"
                                />
                            </div>
                        </template>
                    </template>
                </div>
            </v-flex>
        </v-layout>
        <NsSpinner v-if="busy" />
    </v-container>
</template>

<script>

import * as lodash from 'lodash'

import { ConfigService, RunConfigService, events } from '@nuskin/ns-util'
import { UserService } from '@nuskin/ns-account'
import webLoyalty from '@nuskin/ns-loyalty-web'

import NsPublishEvent from '@/mixin/publish-event.vue'
import NsSpinner from '@/components/common/ns-spinner-color-circle.vue'
import BrushstrokeHeading from '@/components/common/BrushstrokeHeading.vue'
import CardWithSpace from '@/components/common/CardWithSpace.vue'
import NsLandingTierGroupCard from '@/components/Tiering/Landing/LandingTierGroupCard.vue'
import NsLandingWhiteTierCard from '@/components/Tiering/Landing/LandingWhiteTieringCards.vue'
import TieringMixin from '@/components/Tiering/TieringMixin.vue'

import ReplaceLocale from '@/services/replace_locale.js'

const REWARD_CARDS = [
    {
        icon: require('@/assets/rewards-coin.blue.svg'),
        title: 'rewards.enjoyLogoTitle',
        text: 'rewards.enjoyLogoText'
    },
    {
        icon: require('@/assets/Icon_HowToEarn.svg'),
        title: 'rewards.enjoyDollarsTitle',
        text: 'rewards.enjoyDollarsText'
    },
    {
        icon: require('@/assets/Icon_Bag_blue.svg'),
        title: 'rewards.shoppingBagTitle',
        text: 'rewards.shoppingBagText'
    },
    {
        icon: require('@/assets/Icon_Gift_blue.svg'),
        title: 'rewards.giftTitle',
        text: 'rewards.giftText'
    }
]

// const VIEW_WIDTH_MOBILE = 375
const VIEW_WIDTH_TABLET = 768
const VIEW_WIDTH_DESKTOP = 1440

export default {
    name: 'Landing',
    components: {
        NsSpinner,
        BrushstrokeHeading,
        CardWithSpace,
        'ns-tier-group-card': NsLandingTierGroupCard,
        'ns-white-tier-card': NsLandingWhiteTierCard
    },
    mixins: [
        NsPublishEvent,
        TieringMixin
    ],
    computed: {
        isMobile () {
            return this.windowWidth < VIEW_WIDTH_TABLET
        },
        isTablet () {
            return this.windowWidth >= VIEW_WIDTH_TABLET && this.windowWidth < VIEW_WIDTH_DESKTOP
        },
        layout () {
            let layout

            if (this.isMobile) {
                layout = 'mobile'
            } else if (this.isTablet) {
                layout = 'tablet'
            } else {
                layout = 'desktop'
            }

            return layout
        },
        pageWidthIndex () {
            const PAGE_BREAKPOINTS = [
                this.$vuetify.breakpoint.xs,
                this.$vuetify.breakpoint.sm,
                this.$vuetify.breakpoint.md,
                this.$vuetify.breakpoint.lg,
                this.$vuetify.breakpoint.xl
            ]

            return PAGE_BREAKPOINTS.findIndex((breakpoint) => breakpoint)
        }
    },
    data () {
        return {
            aemText: {},
            baseId: 'loyalty-app-landing',
            busy: true,
            canOptIn: true,
            language: '',
            market: '',
            productImage: '',
            dashboardPage: '#',
            pageLogo: '',
            faqPage: '#',
            inMaintenanceMode: false,
            loginPage: '#',
            loginStatus: false,
            optInStatus: false,
            outOfMarket: false,
            rewardCards: REWARD_CARDS,
            showTiering: false,
            sectionIntroductionImages: [],
            sectionTieringImages: [],
            tiering: [],
            tieringLinks: {},
            userTier: null,
            windowWidth: 0
        }
    },
    methods: {
        getBackgroundImageByWidth (container) {
            if (container.length > 0) {
                return 'url(' + container[Math.max(0, Math.min(this.pageWidthIndex, container.length - 1))] + ')'
            }

            return 'none'
        },
        async load () {
            try {
                this.busy = true

                const response = await webLoyalty.getMarketConfig()

                if (response) {
                    this.optInStatus = !!response.optInStatus

                    this.setupTiering(response)
                }
            } catch (ex) {
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.error(ex)
                }
            } finally {
                this.busy = false
            }
        },
        openOptInDialog (ev) {
            events.tag(events.loyalty.LOYALTY_START, { context: 'landing' })
            events.publish(events.loyalty.LOYALTY_SIGNUP_OPEN)

            ev.preventDefault()
        },
        setupTiering (response) {
            const tierConfig = require('@/components/Tiering/Landing/landingTiering.json')
            const tieringFac = this.createTieringFactory()
            tieringFac.mergeConfiguration(tierConfig['*'])

            let marketTierConfig = tierConfig[this.market]
            if (!marketTierConfig) {
                marketTierConfig = tierConfig.default
            }
            tieringFac.mergeConfiguration(marketTierConfig)

            tieringFac.mergeTierData(response.tierData, 'name')
            tieringFac.mergeTierGroups(response.entityTypes)

            this.tieringLinks = {
                becomeVipMember: '#',
                optIntoOurSubscriptionProgram: ReplaceLocale.replaceLocale(this.$nsLanguage.getTranslation('subscriptionLink'))
            }

            this.tiering = tieringFac.build()
            this.showTiering = this.tiering.length > 0
        },
        windowResize () {
            this.windowWidth = window.innerWidth
        }
    },
    async beforeMount () {
        if ((typeof loyaltyAuthorConfig) !== 'undefined') {
            // eslint-disable-next-line no-undef
            this.aemText = loyaltyAuthorConfig
        }

        const marketConfig = ConfigService.getMarketConfig()
        const runConfig = RunConfigService.getRunConfig()

        this.language = runConfig.language
        this.market = runConfig.country
        this.pageLogo = marketConfig.loyalty.landingPageLogo || '/content/dam/apps/loyalty/global/enJoy_nuskinrewards_white.svg'
        this.inMaintenanceMode = marketConfig.loyalty.inMaintenanceMode
        ReplaceLocale.initialize(this.language, this.market)

        const locale = this.language + '_' + this.market

        const templateData = {
            language: this.language,
            market: this.market,
            destination: encodeURIComponent(window.location.href),
            cancel: encodeURIComponent(window.location.href)
        }

        this.sectionIntroductionImages = [
            '/content/dam/apps/loyalty/' + locale + '/sectionJoinImage_xs.png',
            '/content/dam/apps/loyalty/' + locale + '/sectionJoinImage_sm.png',
            '/content/dam/apps/loyalty/' + locale + '/sectionJoinImage_md.png',
            '/content/dam/apps/loyalty/' + locale + '/sectionJoinImage_lg.png',
            '/content/dam/apps/loyalty/' + locale + '/sectionJoinImage_xl.png'
        ]

        this.sectionTieringImages = [
            '/content/dam/apps/loyalty/' + locale + '/sectionTieringImage.png'
        ]

        this.dashboardPage = ReplaceLocale.replaceLocale(marketConfig.loyalty.dashboardPage)
        if ((typeof this.aemText.faq) !== 'undefined' && (typeof this.aemText.faq.faqPage) !== 'undefined') {
            this.faqPage = ReplaceLocale.replaceLocale(this.aemText.faq.faqPage) + '.html'
        }

        // eslint-disable-next-line no-template-curly-in-string
        this.loginPage = lodash.template('/content/login/corporate/${language}.html?destination=${destination}&cancel=${cancel}&market=${market}')(templateData)

        this.loginStatus = UserService.isLoggedIn()
        if (this.loginStatus) {
            const user = await UserService.getUser()

            // Check for out of market:
            this.outOfMarket = (this.market !== user.countryCd)
        }

        if (this.inMaintenanceMode) {
            this.busy = false
        } else {
            this.canOptIn = await webLoyalty.loyaltyCanOptIn()
            this.load()
        }

        events.subscribe(events.loyalty.LOYALTY_SIGNUP_UPDATE, () => {
            window.open(this.dashboardPage, '_self')
        })

        window.addEventListener('resize', this.windowResize)
        this.windowResize()
    },
    destroyed () {
        window.removeEventListener('resize', this.windowResize)
    }
}

</script>

<style lang="scss" scoped>

@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/buttons";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";

@import "~@/scss/view-size.mixins";
@import "~@/scss/fonts";

$color-black: map.get(colors.$baseColors, black);
$color-blue: #02B2DF;
$color-gray-38: map.get(colors.$baseColors, blackhole);
$color-gray-77: #4D4D4D;
$color-gray-84: #545454;
$color-gray-102: map.get(colors.$baseColors, dryink);
$color-gray-151: map.get(colors.$baseColors, ash);
$color-gray-207: map.get(colors.$baseColors, steel);
$color-gray-248: #F7F8F8;
$color-white: #FFFFFF;

%section-background-image {
    background-attachment: local;
    background-origin: padding-box;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
}

$view-width: 375px;

* {
    box-sizing: border-box;
}

%flex-justify-end {
    display: flex;
    justify-content: flex-end;
}

%flex-justify-center {
    display: flex;
    justify-content: center;
}

.maintenance-mode {
    margin-top: 15px;
}

.box-center {
    margin-left: auto;
    margin-right: auto;
}

.box-shadow-none {
    box-shadow: none;
}

.corners-rounded-large {
    border-radius: 20px;
}

.busy-indicator {
    width: 14px;
    height: 14px;
}

.tx-color-white {
    color: $color-white !important;
}

.tx-em-bold-600 {
    ::v-deep {
        b, em, i {
            @extend %font-primary;
            font-style: italic;
            font-weight: 600;
        }
    }
}

.tx-em-bold-700 {
    ::v-deep {
        b, em, i {
            @extend %font-primary;
            font-style: italic;
            font-weight: 700;
        }
    }
}

.tx-em-bold-800 {
    ::v-deep {
        b, em, i {
            @extend %font-primary;
            font-style: italic;
            font-weight: 800;
        }
    }
}

.tx-em-text-uppercase {
    ::v-deep {
        b, em, i {
            text-transform: uppercase;
        }
    }
}

.main {
    position: relative;
    min-width: $view-width;
}

.hero-footer {
    display: flex;
}

.section {
    overflow: hidden;
    padding: 0;

    &.solid-dark {
        background-color: $color-gray-77;
        color: $color-white;
    }

    &.solid-light {
        background-color: $color-gray-248;
        color: $color-black;
    }

    .actions {
        display: flex;
        justify-content: center;
    }

    .button {
        width: 100%;
        height: 40px;
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 1.08px;
        text-transform: uppercase;
    }

    .disclaimers {
        list-style: none;

        ::v-deep {
            li {
                @include global.a-desktop-expressive();

                padding-top: 8px;
                color: #FFFFFF;
                font-size: 14px;
                text-align: initial;
            }
        }
    }

    .section-container {
        position: relative;

        @include view-mobile() {
            $section-width: 375px;

            width: $section-width !important;
            max-width: $section-width;
        }

        @include view-tablet() {
            $section-width: 716px;

            width: $section-width !important;
            max-width: $section-width;
        }

        @include view-desktop() {
            $section-width: 1004px;

            width: $section-width !important;
            max-width: $section-width;
        }
    }

    .section-box-max {
        max-width: 290px;
        margin-left: auto;
        margin-right: auto;
    }

    .section-text {
        ::v-deep {
            p {
                margin: 0;
                margin-top: 16px;
                color: inherit;
                font-family: inherit;
                font-size: inherit;
                font-weight: inherit;
                line-height: inherit;
            }
            p:first-child {
                margin-top: 0;
            }
        }
    }

    &.section-introduction {
        @extend %section-background-image;

        background-color: $color-blue;
        border: 1px solid ;
        color: $color-white;

        .vertical_center{
            line-height: 12px;
        }

        .section-content-container {
            text-align: left;
        }

        .section-heading {
            color: inherit;
            text-align: left;
        }

        .section-text {
            @include global.title-bold();
            line-height: 1.3125em;
            color: inherit;
        }

        .semitransparent-box {
            border: 1px solid white;
            background-color: rgba(0,0,0,0.7);
            padding: 15px;
            width: 688px;
            font-size: 18px;
        }

        .button-container {
            width: 100%;
        }

        .product-logo {
            align-self: flex-end;
            right: 0;
            width: 268px;
            margin-top: 46px;
            text-align: right;

            img {
                vertical-align: bottom;
                width: 100%;
                height: auto;
            }
        }

        .market_unavailable{
            border: 1px solid #FFF;
            padding: 10px 10px 15px 10px;
            background-color: rgba(0,0,0,.7);
            color: #FFF;
            width: 100%;
            text-align: center;
        }
    }

    &.section-rewards {
        background-color: $color-white;
        color: $color-gray-38;

        .section-heading {
            color: $color-gray-38;
            font-size: 22px;
            font-style: italic;
            font-weight: 400;
        }

        .section-text {
            max-width: 660px;
            font-size: 18px;
            font-weight: 300;
            line-height: 1.1666em;
        }
    }

    .faq-link {
        font-size: 14px;
    }

    .faq-link a{
        text-decoration: underline;
        font-size: 14px;
        color: #00b5e2;
        cursor: pointer;
    }

    &.section-group-tiering {
        $section-width: 400px;
        $section-padding-horizontal: 24px;

        @extend %section-background-image;

        margin-top: 0;
        margin-bottom: 0;
        padding: 0;
        background-color: #02B5E2;
        background-image: linear-gradient(225deg, #02B5E2 0%, #427D9F 100%);
        color: $color-white;

        .section-container {
            padding: 32px 16px 60px;
        }

        .section-heading {
            font-size: 22px;
            font-style: italic;
            font-weight: 400;
        }

        .section-text {
            color: inherit;
            font-size: 16px;
            font-weight: 600;
            line-height: 1.1666em;
        }

        .section-content {
            text-align: left;
        }

        .tiering-container {
            margin-left: auto;
            margin-right: auto;

            &:first-child {
                margin-top: 0;
            }
        }

        .tier-card {
            display: block;
            margin-left: auto;
            margin-right: auto;
        }
    }
}

.s-card {
    $card-gutter: 8px;
    $card-icon-width: 50px;
    $card-icon-height: 50px;

    margin: 0;
    padding: 40px 8px 48px;
    background-color: transparent;
    border-radius: 0;
    color: $color-gray-38;

    p:last-of-type {
        margin-bottom: 0;
    }

    .card-icon {
        vertical-align: top;
        width: auto;
        height: $card-icon-height;
    }

    .card-title {
        font-size: 14px;
        font-weight: 800;
        line-height: 1.28em;
        text-align: center;
        text-transform: uppercase;
        word-break: break-word;

        & h1,
        & h2,
        & h3,
        & h4,
        & h5,
        & h6 {
            display: block;
            width: 100%;
            color: $color-gray-38 !important;
            font-size: 14px !important;
            font-weight: 700 !important;
            line-height: 1.28em !important;
            text-align: center !important;
            text-transform: uppercase !important;
        }
    }

    .card-text {
        color: $color-gray-38;
        font-size: 16px;
        font-weight: 300;
        text-align: center;
        line-height: 1.3125em;

        & p {
            font-family: inherit;
            font-size: inherit;
            font-weight: inherit;
        }
    }
}

@include view-mobile() {
    .section {

        .hero-footer {
            flex-direction: column-reverse;

            .disclaimers {
                text-align: left;
                padding: 20px 0 0 0;
                font-size: 12px;
                ::v-deep {
                    li {
                        font-size: 12px;
                    }
                }
            }

            .product-logo {
                margin-top: 20px;
            }

        }

        .section-box-max {
            max-width: 290px;
        }

        .section-heading {
            font-size: 36px;
        }

        &.section-introduction {
            min-height: 444px;

            .section-container {
                padding: 10px 20px;
            }

            .hero-content-container {
                display: flex;
                flex-direction: column;
            }

            .semitransparent-box {
                width: auto;
            }

            .maintenance-mode {
                font-size: 12px;
            }

            .section-content-container {
                margin-top: 10px !important;
                text-align: center;

                &.section-text {
                    margin-top: 0px !important;
                }
            }

            .section-heading {
                line-height: 1.44em;
                text-align: center;
            }

            .section-text {
                font-size: 12px;
            }

            .button-container {
                width: 100%;
            }

            .product-logo {
                position: static;
                bottom: 15px;
                left: 0;
                right: 0;
                width: auto;
                text-align: center;

                img {
                    width: 166px;
                    height: auto;
                }
            }
        }

        &.section-rewards {
            .section-container {
                padding: 32px 16px;
            }

            .section-heading {
                font-size: 22px;
            }

            .section-text {
                max-width: 390px !important;
            }
        }

        &.section-group-tiering {
            .section-content {
                padding-top: 40px;
            }

            .tiering-container {
                margin-top: 25px;
            }
        }
    }

    .s-card-container {
        flex-basis: 290px;
        flex-grow: 0;
        flex-shrink: 0;
    }
}

// --- Medium (960 - 1263)

@include view-tablet() {
    .section {
        .disclaimers {
            bottom: 40px;
        }

        .section-box-max {
            max-width: 668px;
        }

        .section-text {
            p {
                margin-top: 20px;
            }
        }

        .section-container {

            .hero-footer {
                justify-content: space-between;

                .disclaimers {
                    align-self: flex-end;
                    text-align: left;
                    padding-left: 0;
                    width: 50%;
                }
            }
        }

        &.section-introduction {
            min-height: 589px;

            .section-container {
                padding: 40px 0;
            }

            .hero-content-container {
                display: flex;
                flex-direction: column;
            }

            .section-content-container {
                max-width: 420px;
                margin-bottom: 32px;
            }

            .section-heading {
                font-size: 80px;
                line-height: 1.0625em;
            }

            .button-container {
                width: 75%;
            }

            .product-logo {
                bottom: 40px;
            }
        }

        &.section-rewards {
            .section-container {
                padding: 40px 40px;
            }

            .section-heading {
                font-size: 22px;
            }

            .section-text {
                max-width: 690px !important;
            }

            &.semitransparent-box {
                width: 688px;
            }
        }

        &.section-group-tiering {
            .section-container {
                width: 100% !important;
                max-width: 768px;
                padding: 40px 0 46px
            }

            .section-content {
                padding-top: 10px;
            }
        }
    }

    .s-card-container {
        flex-basis: 50% !important;
        flex-grow: 0;
        flex-shrink: 0;
    }

    .s-card {
        padding: 60px 32px;
    }
}

// --- Large (1264 - 1903)

@include view-desktop() {
    .section {
        .section-box-max {
            max-width: none;
        }

        .section-container {

            .hero-footer {
                justify-content: space-between;

                .disclaimers {
                    align-self: flex-end;
                    text-align: left;
                    padding-left: 0;
                    width: 50%;
                }
            }
        }

        &.section-introduction {
            min-height: 589px;
            background-size: auto 100%;

            .section-container {
                padding: 40px 0;
            }

            .hero-content-container {
                display: flex;
                flex-direction: column;
            }

            .section-content-container {
                max-width: 420px;
                margin-bottom: 32px;
            }

            .section-heading {
                font-size: 80px;
                line-height: 1.0625em;
            }

            .button-container {
                width: 75%;
            }

            .product-logo {
                bottom: 40px;
            }
        }

        &.section-rewards {
            .section-container {
                padding: 44px 40px;
            }

            .section-text {
                max-width: 660px !important;
            }
        }

        &.section-group-tiering {
            .section-container {
                padding: 40px 0 46px
            }
        }
    }

    .s-card-container {
        flex-basis: 205px !important;
        flex-grow: 0;
        flex-shrink: 0;
    }

    .s-card {
        padding: 32px 16px 72px;
    }
}

</style>
